/**
 * OrderDetails.tsx
 *
 * This component displays the details of an order, allowing the user to view the order status and history,
 * and add comments or update the status. It includes the ability to fetch order details by ID, handle
 * status changes, and submit history updates.
 *
 * Key Features:
 * - Fetches and displays order details based on the provided order ID.
 * - Allows users to update the order status and add history comments.
 * - Includes loading indicators for both data fetching and history submission.
 *
 * File Name: OrderDetails.tsx
 * Date: 15-08-24
 */

import React, { FormEvent, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import BreadCrumb from "../../Components/GlobalComponents/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import SplashScreen from "src/Components/SplashScreen";
import { IOrderDetails } from "src/types/GlobalInterfaces";
import DetailView from "src/Components/GlobalComponents/DetailView";
import SimpleInput from "src/Components/SimpleInput";
import { CircularProgress } from "@mui/material";
import generatePDF from "react-to-pdf";
import { Margin } from "react-to-pdf";
import ConfirmationModal from "src/Components/GlobalComponents/ConfirmationModal";
import { updateIndicators } from "src/Store/redDots";

const OrderDetails: React.FC = () => {
  const tableRef: any = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderId = "" } = useParams();

  const [isLoader, setIsLoader] = useState<boolean>(false); // Loader for fetching order details
  const [postLoader, setPostIsLoader] = useState<boolean>(false); // Loader for submitting history update
  const [item, setItem] = useState<IOrderDetails | null>(null); // State to store order details
  const [changeHistory, setChangeHistory] = useState<boolean>(false); // State to trigger history refresh
  const [status, setStatus] = useState<string>(""); // State for selected status
  const [comment, setComment] = useState<string>(""); // State for comment input
  const [deleteStates, setDeleteStates] = useState<{
    loader: boolean;
    modal: boolean;
  }>({
    loader: false,
    modal: false,
  });
  const deleteModal = () => {
    setDeleteStates({
      loader: false,
      modal: false,
    });
  };
  const handleDeleteOrder = async () => {
    setDeleteStates((prev) => ({ ...prev, loader: true }));
    const { res, err } = await httpRequest<{ message: string }>({
      method: "delete",
      path: `/diagnostic-order/delete/${orderId}`,
      params: {},
    });
    if (res) {
      toast(res?.message || "Success", { type: "success" });
      deleteModal();
      setItem((prev) => (prev ? { ...prev, status } : null));
    } else {
      toast(err, { type: "error" });
    }
    setDeleteStates((prev) => ({ ...prev, loader: false }));
  };

  // Handles status change in the dropdown
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value);
  };

  // API_CALL: Submits the updated history (status and comment) for the order
  const historySubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (item?.status === "cancelled") {
      return;
    }
    setPostIsLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "post",
      path: `/diagnostic-order/history/status/${orderId}`,
      params: {
        status: status,
        comment: comment,
      },
    });
    if (res) {
      toast(res?.message || "Success", { type: "success" });
      setComment(""); // Clear comment input after successful submission
      setChangeHistory((prev) => !prev); // Toggle changeHistory to refresh order history
      setItem((prev) => prev && { ...prev, status });
    } else {
      toast(err, { type: "error" });
    }
    setPostIsLoader(false);
  };

  // API_CALL: Fetches order details based on the order ID
  const fetchOrderById = async (_id: string) => {
    setIsLoader(true);
    const { res, err } = await httpRequest<IOrderDetails>({
      path: `/diagnostic-order/order/${_id}`,
    });
    if (res) {
      setItem(res);
      setStatus(res.status || "");
      let indicator = res?.adminNotified;
      if (!indicator) {
        dispatch(updateIndicators({ diagnosticOrder: -1 }));
      }
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  // Fetch order details when the component mounts or when orderId changes
  useEffect(() => {
    fetchOrderById(orderId);
  }, [orderId]);

  return (
    <>
      {isLoader && <SplashScreen />}
      <ConfirmationModal
        show={deleteStates.modal}
        onClose={deleteModal}
        title="Are you sure you want to delete?"
        leftBtnText="No"
        rightBtnText="Yes"
        onConfirm={handleDeleteOrder}
        loader={deleteStates.loader}
      />
      <BreadCrumb
        current="Test Orders"
        previous="dashboard"
        navigateUrl="/admin"
        btnShow={true}
        isUpsert={true}
        pharmaOrderIcon={true}
        onPress={() => navigate(-1)}
        onPressPrint={() => {
          if (tableRef && tableRef.current) {
            const printContent = tableRef.current;
            const newWindow = window.open("", "_blank", "width=800,height=600");

            if (newWindow) {
              newWindow.document.write(`
                      <html>
                          <head>
                              <title>Print Diagnostic Order Details</title>
                              <style>
                                  * {
                                      margin: 0;
                                      padding: 0;
                                      box-sizing: border-box;
                                  }
                                  body {
                                      font-family: Arial, sans-serif;
                                      -webkit-print-color-adjust: exact; /* Ensures background colors are printed */
                                      print-color-adjust: exact; /* For non-WebKit browsers */
                                  }
                                  .order-detail-container {
                                      width: 100%;
                                      background-color: #EEEEEE;
                                      padding: 0.8rem 1.2rem;
                                      border-radius: 10px;
                                      margin: 0.8rem;
                                      -webkit-print-color-adjust: exact; /* Ensure color is rendered in print */
                                  }
                                  .order-detail-child {
                                      border-radius: 10px;
                                      background-color: #CCD6DF;
                                      -webkit-print-color-adjust: exact;
                                  }
                                  .order-detail-head {
                                      padding: 10px;
                                  }
                                  .order-no {
                                      color: #707070;
                                  }
                                  .order-add-table {
                                      display: grid;
                                      grid-template-columns: 20% 20% 20% 20% 20%;
                                      text-align: center;
                                      background-color: #00443F;
                                      padding: 8px 0px;
                                      -webkit-print-color-adjust: exact;
                                  }
                                  .sub-order-add-table {
                                      display: grid;
                                      grid-template-columns: 20% 20% 20% 20% 20%;
                                      text-align: center;
                                      color: #949494;
                                  }
                                  .sub-order-add-table p {
                                      height: 40px;
                                      border-right: 1px solid #949494;
                                      display: flex;
                                      justify-content: center;
                                      align-items: center;
                                      -webkit-print-color-adjust: exact;
                                  }
                                  .order-add-table-second {
                                      padding: 8px 0px;
                                      grid-template-columns: 34% 33% 33%;
                                  }
                                  .sub-order-add-table-second p {
                                      padding: 8px 0px !important;
                                      border: 1px solid #949494;
                                      color: #646464 !important;
                                  }
                                  .sub-order-add-table-second {
                                      grid-template-columns: 34% 33% 33%;
                                  }
                                  .Order-product-name-bdl {
                                      border-left: 1px solid #949494;
                                  }
                                      p{
                                      font-size:12px;
                                      font-weight:500;
                                      }
                                      .order-detail-main {
display: grid
;
grid-template-columns: 32% 32% 32%;
justify-content: space-between;
margin: 15px 0px;
}
h3{
                                      font-size:15px;
                                      font-weight:500;
                                      }
                                          h4{
                                      font-size:13px;
                                      font-weight:500;
                                      }
                                      .total-table {
display: grid;
grid-template-columns: 80% 20%;
}

.total-table p {
border: 1px solid #b9b9b9;
padding: 5px 10px;
}
.order-no-cust{
padding-bottom:1rem;
}
.pharma-order-detail-prescriptions {
display: flex;
flex-wrap: wrap;
gap: 1rem;
margin-top: 0;
margin-bottom: 0.5rem;
}

.pharma-order-detail-prescriptions img {
width: 3vw;
aspect-ratio: 1 / 1;
}
                              </style>
                          </head>
                          <body>
                              ${printContent.innerHTML}
                          </body>
                      </html>
                  `);

              newWindow.document.close(); // Close the document to apply changes
              newWindow.focus(); // Focus on the new window

              setTimeout(() => {
                console.log("Triggering print...");
                newWindow.print(); // Trigger print dialog
              }, 500); // Delay to ensure content is fully loaded
            } else {
              console.error(
                "Unable to open a new window. It might be blocked by the browser."
              );
            }
          } else {
            console.error("tableRef or tableRef.current is not defined.");
          }
        }}
        onPressExport={() => {
          if (tableRef && tableRef.current) {
            generatePDF(tableRef, {
              filename: "dianosticOrders.pdf",
              canvas: { mimeType: "image/png" },
              page: {
                margin: Margin.SMALL,
              },
            });
          }
        }}
        onDeleteItem={() =>
          setDeleteStates((prev) => ({ ...prev, modal: true }))
        }
      />
      {item && (
        <div className="order-detail-container">
          <div className="w-full print-wind" ref={tableRef}>
            <DetailView
              item={item}
              id={orderId}
              changeHistory={changeHistory}
              isAdmin={true}
            />
          </div>
          <div>
            <div className="order-history">
              <h2 className="font-size-20px text-[#303030] mt-4 font-Poppins-Medium">
                Add History
              </h2>
            </div>
            <form onSubmit={historySubmit}>
              <div className="order-status-outer">
                <span className="font-size-15px text-[#303030] font-Poppins-SemiBold">
                  Order Status
                </span>
                <div>
                  <select
                    onChange={handleStatusChange}
                    disabled={
                      item.status === "cancelled" || item.status === "deleted" || item.status === "completed"
                    }
                    value={status}
                    className="create-role-input font-size-16px font-Poppins-Regular"
                    id="order-status-select"
                  >
                    <option disabled value="pending" id="pending-option">
                      Pending
                    </option>
                    {item.status === "deleted" && (
                      <option value="deleted" id="deleted-option">
                        Deleted
                      </option>
                    )}
                    <option value="cancelled" disabled={item.status !== "pending"} id="cancelled-option">
                      Cancelled
                    </option>
                    <option value="completed" id="completed-option">
                      Completed
                    </option>
                  </select>
                </div>
              </div>
              <div className="order-comments">
                <span className="font-size-15px text-[#303030] font-Poppins-SemiBold">
                  Comment
                </span>
                <div className="order-invoice-select">
                  <SimpleInput
                    isRequired={true}
                    value={comment}
                    type="textArea"
                    rows={7}
                    placeholder="Comment"
                    onChange={(val) => setComment(val)}
                    disabled={
                      item.status === "cancelled" || item.status === "deleted" || item.status === "completed"
                    }
                    id="comment-input"
                    isLimit={500}
                    maxLength={500}
                  />
                </div>
              </div>
              <div className="order-add-history flex justify-end">
                <button
                  type="submit"
                  disabled={
                    postLoader ||
                    item.status === "cancelled" ||
                    item.status === "deleted" ||
                    item.status === "completed"
                  }
                  id="add-history-btn"
                  className="bg-[#00443F] flex items-center justify-center confirm-btn"
                  style={{ minWidth: 100 }}
                >
                  {postLoader ? (
                    <CircularProgress
                      style={{ width: 14, height: 14, color: "#fff" }}
                    />
                  ) : (
                    <>
                      <div className="plus-icon-order">
                        <FontAwesomeIcon icon={faPlus} />
                      </div>
                      <span className="font-size-12px font-Poppins-SemiBold ">
                        Add History
                      </span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderDetails;
